import React, { useState, useEffect } from 'react';
import { 
  Flame, Plus, Trash2, Image, Save, ArrowLeft, 
  Lock, Eye, EyeOff, Users, Package, Edit2, X, Upload,
  ChevronDown, ChevronUp, Loader2, RefreshCw, Kanban,
  FileText, BarChart3
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CRMKanban from '../components/CRMKanban';
import QuotesTab from '../components/QuotesTab';
import SalesDashboard from '../components/SalesDashboard';
import NotificationBell from '../components/NotificationBell';
import { normalizeAssetUrl } from '../utils/assetUrl';

const ADMIN_PASSWORD = 'rotativa2024';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data states
  const [customerPhotos, setCustomerPhotos] = useState([]);
  const [products, setProducts] = useState([]);

  // Form states
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  
  const [newCustomer, setNewCustomer] = useState({
    type: 'image',
    url: '',
    customerName: '',
    caption: '',
    location: ''
  });

  // Check auth and load data
  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Seed data first (only creates if empty)
      await axios.post(`${API}/seed`);
      
      // Load customers and products
      const [customersRes, productsRes] = await Promise.all([
        axios.get(`${API}/customers`),
        axios.get(`${API}/products`)
      ]);
      
      setCustomerPhotos(customersRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showMessage('Erro ao carregar dados', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
      setLoginError('');
    } else {
      setLoginError('Senha incorreta!');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
    setPassword('');
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Upload file function
  const handleFileUpload = async (file, callback) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        // Save RELATIVE URL so it survives backend preview URL changes (re-deploys, forks, etc.)
        // The frontend prepends BACKEND_URL when rendering — see normalizeAssetUrl helper.
        callback(response.data.url, response.data.type);
        showMessage('Arquivo enviado!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showMessage('Erro ao enviar arquivo', 'error');
    } finally {
      setUploading(false);
    }
  };

  // ==================== CUSTOMER FUNCTIONS ====================

  const handleAddCustomer = async () => {
    if (!newCustomer.url) {
      showMessage('Faça upload ou insira uma URL', 'error');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/customers`, newCustomer);
      setCustomerPhotos([...customerPhotos, response.data]);
      setNewCustomer({ type: 'image', url: '', customerName: '', caption: '', location: '' });
      setShowCustomerForm(false);
      showMessage('Cliente adicionado!');
    } catch (error) {
      console.error('Error adding customer:', error);
      showMessage('Erro ao adicionar', 'error');
    }
  };

  const handleUpdateCustomer = async () => {
    if (!editingCustomer) return;
    
    try {
      const response = await axios.put(`${API}/customers/${editingCustomer.id}`, editingCustomer);
      setCustomerPhotos(customerPhotos.map(c => c.id === editingCustomer.id ? response.data : c));
      setEditingCustomer(null);
      showMessage('Cliente atualizado!');
    } catch (error) {
      console.error('Error updating customer:', error);
      showMessage('Erro ao atualizar', 'error');
    }
  };

  const handleDeleteCustomer = async (id) => {
    if (!window.confirm('Excluir esta foto?')) return;
    
    try {
      await axios.delete(`${API}/customers/${id}`);
      setCustomerPhotos(customerPhotos.filter(p => p.id !== id));
      showMessage('Cliente removido!');
    } catch (error) {
      console.error('Error deleting customer:', error);
      showMessage('Erro ao excluir', 'error');
    }
  };

  const handleCustomerFileChange = (e, isEditing = false) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, (url, type) => {
        if (isEditing && editingCustomer) {
          setEditingCustomer({ ...editingCustomer, url, type });
        } else {
          setNewCustomer({ ...newCustomer, url, type });
        }
      });
    }
  };

  // ==================== PRODUCT FUNCTIONS ====================

  const handleUpdateProduct = async (productId, updates) => {
    try {
      const response = await axios.put(`${API}/products/${productId}`, updates);
      setProducts(products.map(p => p.id === productId ? response.data : p));
      showMessage('Produto atualizado!');
    } catch (error) {
      console.error('Error updating product:', error);
      showMessage('Erro ao atualizar', 'error');
    }
  };

  const handleProductFileChange = (e, productId) => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file, async (url) => {
        const product = products.find(p => p.id === productId);
        if (product) {
          const newImages = [...product.images, url];
          await handleUpdateProduct(productId, { images: newImages });
        }
      });
    }
  };

  const handleRemoveProductImage = async (productId, imageIndex) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const newImages = product.images.filter((_, i) => i !== imageIndex);
      await handleUpdateProduct(productId, { images: newImages });
    }
  };

  const handleAddProduct = async () => {
    try {
      const response = await axios.post(`${API}/products`, {
        name: "Novo Produto",
        description: "Descrição do produto",
        features: ["Característica 1"],
        images: [],
        price: "Consulte",
        tag: "",
        order: products.length + 1
      });
      setProducts([...products, response.data]);
      setExpandedProduct(response.data.id);
      showMessage('Produto criado!');
    } catch (error) {
      console.error('Error adding product:', error);
      showMessage('Erro ao criar produto', 'error');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Excluir este produto?')) return;
    
    try {
      await axios.delete(`${API}/products/${id}`);
      setProducts(products.filter(p => p.id !== id));
      showMessage('Produto removido!');
    } catch (error) {
      console.error('Error deleting product:', error);
      showMessage('Erro ao excluir', 'error');
    }
  };

  // ==================== LOGIN SCREEN ====================

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center p-4">
        <div className="bg-stone-800 rounded-2xl p-8 w-full max-w-md border border-stone-700">
          <div className="flex items-center justify-center gap-2 mb-8">
            <Flame className="w-10 h-10 text-amber-500" />
            <span className="text-2xl font-bold text-white">Admin</span>
          </div>
          
          <form onSubmit={handleLogin}>
            <div className="mb-6">
              <label className="block text-stone-400 text-sm mb-2">Senha de Acesso</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Digite a senha"
                  className="w-full bg-stone-900 border border-stone-600 rounded-lg pl-10 pr-12 py-3 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {loginError && <p className="text-red-500 text-sm mt-2">{loginError}</p>}
            </div>
            
            <button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-stone-900 py-3 rounded-lg font-bold transition-colors">
              Entrar
            </button>
          </form>
          
          <button onClick={() => navigate('/')} className="w-full mt-4 text-stone-400 hover:text-white transition-colors flex items-center justify-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Voltar ao Site
          </button>
        </div>
      </div>
    );
  }

  // ==================== ADMIN DASHBOARD ====================

  return (
    <div className="min-h-screen bg-stone-900">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/')} className="text-stone-400 hover:text-white transition-colors">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div className="flex items-center gap-2">
                <Flame className="w-8 h-8 text-amber-500" />
                <span className="text-xl font-bold text-white hidden sm:block">Painel Admin</span>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {uploading && (
                <span className="text-amber-400 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                </span>
              )}
              {message.text && (
                <span className={`text-sm px-3 py-1 rounded-full ${message.type === 'error' ? 'text-red-400 bg-red-500/10' : 'text-green-400 bg-green-500/10'}`}>
                  {message.text}
                </span>
              )}
              <button onClick={loadData} className="text-stone-400 hover:text-white p-2" title="Recarregar">
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <NotificationBell onNavigateToCRM={() => setActiveTab('crm')} />
              <button onClick={handleLogout} className="bg-stone-700 hover:bg-stone-600 text-white px-4 py-2 rounded-lg transition-colors">
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-stone-800/50 border-b border-stone-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 sm:gap-4 overflow-x-auto">
            <button
              data-testid="tab-dashboard"
              onClick={() => setActiveTab('dashboard')}
              className={`py-4 px-3 sm:px-4 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'dashboard' ? 'text-amber-500 border-amber-500' : 'text-stone-400 border-transparent hover:text-white'}`}
            >
              <BarChart3 className="w-5 h-5" /> Painel
            </button>
            <button
              data-testid="tab-crm"
              onClick={() => setActiveTab('crm')}
              className={`py-4 px-3 sm:px-4 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'crm' ? 'text-amber-500 border-amber-500' : 'text-stone-400 border-transparent hover:text-white'}`}
            >
              <Kanban className="w-5 h-5" /> CRM
            </button>
            <button
              data-testid="tab-quotes"
              onClick={() => setActiveTab('quotes')}
              className={`py-4 px-3 sm:px-4 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'quotes' ? 'text-amber-500 border-amber-500' : 'text-stone-400 border-transparent hover:text-white'}`}
            >
              <FileText className="w-5 h-5" /> Orçamentos
            </button>
            <button
              data-testid="tab-customers"
              onClick={() => setActiveTab('customers')}
              className={`py-4 px-3 sm:px-4 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'customers' ? 'text-amber-500 border-amber-500' : 'text-stone-400 border-transparent hover:text-white'}`}
            >
              <Users className="w-5 h-5" /> Clientes
            </button>
            <button
              data-testid="tab-products"
              onClick={() => setActiveTab('products')}
              className={`py-4 px-3 sm:px-4 font-medium flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${activeTab === 'products' ? 'text-amber-500 border-amber-500' : 'text-stone-400 border-transparent hover:text-white'}`}
            >
              <Package className="w-5 h-5" /> Produtos
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
        </div>
      )}

      {/* Main Content */}
      {!loading && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* DASHBOARD TAB */}
          {activeTab === 'dashboard' && <SalesDashboard showMessage={showMessage} />}

          {/* CRM TAB */}
          {activeTab === 'crm' && <CRMKanban showMessage={showMessage} />}

          {/* QUOTES TAB */}
          {activeTab === 'quotes' && <QuotesTab showMessage={showMessage} />}

          {/* CUSTOMERS TAB */}
          {activeTab === 'customers' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Fotos de Clientes</h2>
                  <p className="text-stone-400 text-sm">{customerPhotos.length} foto(s) cadastrada(s)</p>
                </div>
                <button
                  onClick={() => { setShowCustomerForm(true); setEditingCustomer(null); setNewCustomer({ type: 'image', url: '', customerName: '', caption: '', location: '' }); }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  <Plus className="w-5 h-5" /> Adicionar
                </button>
              </div>

              {/* Add/Edit Customer Form */}
              {(showCustomerForm || editingCustomer) && (
                <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">{editingCustomer ? 'Editar Cliente' : 'Nova Foto/Vídeo'}</h3>
                    <button onClick={() => { setShowCustomerForm(false); setEditingCustomer(null); }} className="text-stone-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  {/* Upload Section */}
                  <div className="mb-6">
                    <label className="block text-stone-400 text-sm mb-2">Upload do PC (Foto ou Vídeo)</label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-stone-600 hover:border-amber-500 rounded-lg p-6 text-center transition-colors">
                          <Upload className="w-8 h-8 text-stone-500 mx-auto mb-2" />
                          <p className="text-stone-400 text-sm">Clique para escolher arquivo</p>
                          <p className="text-stone-500 text-xs mt-1">JPG, PNG, MP4, MOV</p>
                        </div>
                        <input type="file" accept="image/*,video/*" onChange={(e) => handleCustomerFileChange(e, !!editingCustomer)} className="hidden" />
                      </label>
                      
                      {(editingCustomer?.url || newCustomer.url) && (
                        <div className="w-32 h-32 bg-stone-700 rounded-lg overflow-hidden flex-shrink-0">
                          {(editingCustomer?.type || newCustomer.type) === 'video' ? (
                            <video src={normalizeAssetUrl(editingCustomer?.url || newCustomer.url)} className="w-full h-full object-cover" muted />
                          ) : (
                            <img src={normalizeAssetUrl(editingCustomer?.url || newCustomer.url)} alt="Preview" className="w-full h-full object-cover" />
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-4">
                      <label className="block text-stone-400 text-sm mb-2">Ou cole uma URL</label>
                      <input
                        type="text"
                        value={editingCustomer?.url || newCustomer.url}
                        onChange={(e) => {
                          const url = e.target.value;
                          const type = url.match(/\.(mp4|mov|avi|webm)$/i) ? 'video' : 'image';
                          if (editingCustomer) setEditingCustomer({ ...editingCustomer, url, type });
                          else setNewCustomer({ ...newCustomer, url, type });
                        }}
                        placeholder="https://..."
                        className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-400 text-sm mb-2">Nome do Cliente</label>
                      <input
                        type="text"
                        value={editingCustomer?.customerName || newCustomer.customerName}
                        onChange={(e) => {
                          if (editingCustomer) setEditingCustomer({ ...editingCustomer, customerName: e.target.value });
                          else setNewCustomer({ ...newCustomer, customerName: e.target.value });
                        }}
                        placeholder="Ex: João Silva"
                        className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-stone-400 text-sm mb-2">Localização</label>
                      <input
                        type="text"
                        value={editingCustomer?.location || newCustomer.location}
                        onChange={(e) => {
                          if (editingCustomer) setEditingCustomer({ ...editingCustomer, location: e.target.value });
                          else setNewCustomer({ ...newCustomer, location: e.target.value });
                        }}
                        placeholder="Ex: Porto Alegre, RS"
                        className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-stone-400 text-sm mb-2">Descrição/Legenda</label>
                      <input
                        type="text"
                        value={editingCustomer?.caption || newCustomer.caption}
                        onChange={(e) => {
                          if (editingCustomer) setEditingCustomer({ ...editingCustomer, caption: e.target.value });
                          else setNewCustomer({ ...newCustomer, caption: e.target.value });
                        }}
                        placeholder="Ex: Costelão perfeito no domingo!"
                        className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white placeholder-stone-500 focus:border-amber-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={editingCustomer ? handleUpdateCustomer : handleAddCustomer}
                    disabled={uploading}
                    className="mt-4 bg-amber-500 hover:bg-amber-600 text-stone-900 px-6 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                  >
                    {editingCustomer ? 'Salvar Alterações' : 'Adicionar'}
                  </button>
                </div>
              )}

              {/* Customer Photos Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {customerPhotos.map((photo) => (
                  <div key={photo.id} className="bg-stone-800 rounded-xl overflow-hidden border border-stone-700">
                    <div className="aspect-video relative">
                      {photo.type === 'video' ? (
                        <video src={normalizeAssetUrl(photo.url)} className="w-full h-full object-cover" muted />
                      ) : (
                        <img src={normalizeAssetUrl(photo.url)} alt={photo.caption} className="w-full h-full object-cover" />
                      )}
                      <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${photo.type === 'video' ? 'bg-blue-600' : 'bg-green-600'} text-white`}>
                        {photo.type === 'video' ? 'Vídeo' : 'Foto'}
                      </span>
                    </div>
                    
                    <div className="p-4">
                      <p className="text-white font-medium">{photo.customerName || 'Cliente'}</p>
                      <p className="text-stone-400 text-sm">{photo.caption}</p>
                      {photo.location && <p className="text-amber-400 text-xs mt-1">{photo.location}</p>}
                      
                      <div className="mt-3 flex items-center gap-3">
                        <button onClick={() => setEditingCustomer(photo)} className="text-amber-400 hover:text-amber-300 text-sm flex items-center gap-1">
                          <Edit2 className="w-4 h-4" /> Editar
                        </button>
                        <button onClick={() => handleDeleteCustomer(photo.id)} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
                          <Trash2 className="w-4 h-4" /> Excluir
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {customerPhotos.length === 0 && (
                <div className="text-center py-12 bg-stone-800 rounded-xl">
                  <Users className="w-12 h-12 text-stone-600 mx-auto mb-3" />
                  <p className="text-stone-400">Nenhuma foto de cliente ainda.</p>
                </div>
              )}
            </div>
          )}

          {/* PRODUCTS TAB */}
          {activeTab === 'products' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">Produtos</h2>
                  <p className="text-stone-400 text-sm">{products.length} produto(s) cadastrado(s)</p>
                </div>
                <button onClick={handleAddProduct} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 transition-colors">
                  <Plus className="w-5 h-5" /> Novo Produto
                </button>
              </div>

              {/* Products List */}
              <div className="space-y-4">
                {products.map((product) => (
                  <div key={product.id} className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden">
                    <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-stone-700/50" onClick={() => setExpandedProduct(expandedProduct === product.id ? null : product.id)}>
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-stone-700 rounded-lg overflow-hidden flex-shrink-0">
                          {product.images?.[0] ? (
                            <img src={normalizeAssetUrl(product.images[0])} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Image className="w-6 h-6 text-stone-500" /></div>
                          )}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold">{product.name}</h3>
                          <p className="text-stone-400 text-sm">{product.images?.length || 0} foto(s) • {product.price}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {product.tag && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full">{product.tag}</span>}
                        {expandedProduct === product.id ? <ChevronUp className="w-5 h-5 text-stone-400" /> : <ChevronDown className="w-5 h-5 text-stone-400" />}
                      </div>
                    </div>

                    {expandedProduct === product.id && (
                      <div className="p-4 border-t border-stone-700 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-stone-400 text-sm mb-2">Nome</label>
                            <input
                              type="text"
                              value={product.name}
                              onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, name: e.target.value } : p))}
                              onBlur={() => handleUpdateProduct(product.id, { name: product.name })}
                              className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-stone-400 text-sm mb-2">Preço</label>
                            <input
                              type="text"
                              value={product.price}
                              onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, price: e.target.value } : p))}
                              onBlur={() => handleUpdateProduct(product.id, { price: product.price })}
                              className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-stone-400 text-sm mb-2">Tag</label>
                            <input
                              type="text"
                              value={product.tag || ''}
                              onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, tag: e.target.value } : p))}
                              onBlur={() => handleUpdateProduct(product.id, { tag: product.tag })}
                              className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-stone-400 text-sm mb-2">Características (vírgula)</label>
                            <input
                              type="text"
                              value={product.features?.join(', ') || ''}
                              onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, features: e.target.value.split(',').map(f => f.trim()) } : p))}
                              onBlur={() => handleUpdateProduct(product.id, { features: product.features })}
                              className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                            />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-stone-400 text-sm mb-2">Descrição</label>
                            <textarea
                              value={product.description}
                              onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, description: e.target.value } : p))}
                              onBlur={() => handleUpdateProduct(product.id, { description: product.description })}
                              rows={2}
                              className="w-full bg-stone-900 border border-stone-600 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none resize-none"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-stone-400 text-sm mb-2">Fotos</label>
                          <div className="flex flex-wrap gap-3">
                            {product.images?.map((img, index) => (
                              <div key={index} className="relative group">
                                <img src={normalizeAssetUrl(img)} alt="" className="w-24 h-24 object-cover rounded-lg" />
                                <button onClick={() => handleRemoveProductImage(product.id, index)} className="absolute -top-2 -right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                            <label className="w-24 h-24 border-2 border-dashed border-stone-600 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-amber-500 transition-colors">
                              <Upload className="w-6 h-6 text-stone-500" />
                              <span className="text-xs text-stone-500 mt-1">Upload</span>
                              <input type="file" accept="image/*" onChange={(e) => handleProductFileChange(e, product.id)} className="hidden" />
                            </label>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-stone-700">
                          <button onClick={() => handleDeleteProduct(product.id)} className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1">
                            <Trash2 className="w-4 h-4" /> Excluir Produto
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      )}
    </div>
  );
};

export default AdminPage;
